/**
 * The ONE function a business service calls to make its write syncable.
 *
 * WHY A HELPER RATHER THAN CALLING `enqueueChange` DIRECTLY
 * -------------------------------------------------------
 * Because the four mistakes that matter here are all easy to make by hand and
 * none of them is obvious at the call site:
 *
 *  - a per-call `newId()` for the changeId, so a retried business operation
 *    publishes a second change and a second device creates a duplicate invoice;
 *  - a `Date.now()` version that resets, so two devices both believe they are
 *    newer and neither converges;
 *  - a hardcoded or inherited businessId, which is the cross-tenant bug;
 *  - an enqueue that takes `db` instead of the live `tx`, silently opening a
 *    second transaction and breaking atomicity.
 *
 * This helper derives the version from the row itself, takes the device from the
 * store, takes the business from the row, and requires the caller's transaction.
 * A service cannot misuse it without a type error.
 *
 * THE VERSION IS THE ROW'S, NOT A CLOCK
 * -------------------------------------
 * `version` is read from the row being written and incremented. Two devices
 * editing the same customer produce 2 and 2, not "whoever's clock is ahead" —
 * which is what makes the conflict model in `conflictModel.ts` meaningful.
 * Timestamps lie across a laptop that was asleep; a counter does not.
 */
import { Dexie } from 'dexie';
import type { Table } from 'dexie';
import type { SyncOutboxEntry } from '@data/db/types';
import type { SyncEntityType } from '@domain/sync/changeModel';
import { newId } from '@platform/ids';
import { enqueueChanges, type EnqueueInput } from './outbox';

const ENTITY_TABLE: Record<SyncEntityType, string> = {
  party: 'parties',
  product: 'products',
  category: 'categories',
  taxRate: 'taxRates',
  stockLocation: 'stockLocations',
  document: 'documents',
  documentLine: 'documentLines',
  documentLink: 'documentLinks',
  payment: 'payments',
  paymentAllocation: 'paymentAllocations',
  expense: 'expenses',
  stockTransaction: 'stockTransactions',
  business: 'businesses',
};

export interface SyncRecorder {
  syncOutbox: Table<SyncOutboxEntry, string>;
  table(name: string): Table<Record<string, unknown>, string>;
}

export interface RecordBusinessMutation {
  readonly businessId: string;
  readonly deviceId: string;
  readonly entityType: SyncEntityType;
  readonly entityId: string;
  readonly operation: 'UPSERT' | 'DELETE';
  /**
   * The version being WRITTEN, not the previous one.
   *
   * The caller has just incremented it, so this is the row's new version. It is
   * passed in rather than read here because the row is in the caller's hands,
   * not in a table this helper can see.
   */
  readonly version: number;
  readonly changedAt: string;
  readonly payload?: Record<string, unknown>;
  readonly deletedAt?: string;
  /**
   * The change id. MINTED HERE, once, when this mutation is first recorded, and
   * stored on the outbox row in the same transaction as the business write.
   *
   * That storage is the whole guarantee: a retry re-sends the STORED id, so it
   * cannot recompute a different one, and no clock or random value is involved
   * at retry time.
   *
   * Pass a value ONLY to re-record a mutation that already has an id (a test
   * republishing one entry, or a caller resuming a known mutation).
   */
  readonly changeId?: string;
}

/**
 * Record a set of related mutations in ONE write, inside the caller's
 * transaction.
 *
 * This is the form a service uses whenever one business operation touches more
 * than one row — a payment and its allocations, a document and its lines and its
 * links, an issue and its stock movements.
 *
 * NOT `async`, and the returned value is a `Dexie.Promise`. Dexie decides a
 * transaction may commit when it can no longer see an outstanding promise OF ITS
 * OWN; an `async` wrapper returns a promise it cannot see, so it can conclude the
 * transaction is finished while this write is still in flight. The write then
 * lands outside the transaction, or throws `PrematureCommitError`, with the
 * business rows already committed and the outbox EMPTY — the worst state this
 * system can reach, because an append-only stock ledger has no later correction.
 *
 * The change id is minted HERE, once per mutation, and stored on the outbox row
 * beside the payload — never derived from something two devices could both
 * compute. That is what makes a republish a no-op while leaving two devices'
 * edits to the same row as two distinct, observable changes.
 */
export function recordBusinessMutations(
  tx: SyncRecorder,
  inputs: readonly RecordBusinessMutation[],
): Dexie.Promise<string[]> {
  const changeIds = inputs.map((i) => i.changeId ?? newId());
  const rows = inputs.map((input, index) => toEnqueueInput(input, changeIds[index] ?? newId()));
  return enqueueChanges(tx.syncOutbox, rows).then(() => {
    const chain = inputs.reduce<Dexie.Promise<void>>(
      (prev, input, index) =>
        prev.then(() => {
          const tableName = ENTITY_TABLE[input.entityType];
          return tableName
            ? tx
                .table(tableName)
                .update(input.entityId, { syncChangeId: changeIds[index] })
                .then(() => undefined)
            : undefined;
        }),
      Dexie.Promise.resolve(undefined),
    );
    return chain.then(() => changeIds);
  });
}

/**
 * Record ONE business mutation, inside the caller's transaction.
 *
 * `recordBusinessMutations` is preferred whenever one operation touches several
 * rows; this is the single-row form, so a service that changes one record does
 * not have to build an array to say so.
 *
 * Returns a `Dexie.Promise` for the same reason as above: an `async` wrapper here
 * would put the write outside the transaction it was supposed to belong to.
 */
export function recordBusinessMutation(
  tx: SyncRecorder,
  input: RecordBusinessMutation,
): Dexie.Promise<string> {
  const changeId = input.changeId ?? newId();
  return enqueueChanges(tx.syncOutbox, [toEnqueueInput(input, changeId)]).then(() => {
    const tableName = ENTITY_TABLE[input.entityType];
    return tableName
      ? tx
          .table(tableName)
          .update(input.entityId, { syncChangeId: changeId })
          .then(() => changeId)
      : changeId;
  });
}

function toEnqueueInput(input: RecordBusinessMutation, changeId: string): EnqueueInput {
  return {
    changeId,
    businessId: input.businessId,
    deviceId: input.deviceId,
    entityType: input.entityType,
    entityId: input.entityId,
    operation: input.operation,
    version: input.version,
    changedAt: input.changedAt,
    payload: input.payload,
    deletedAt: input.deletedAt,
  };
}

/**
 * The next version for a row.
 *
 * Rows created before sync existed have no `syncVersion`, which reads as 0 and
 * becomes 1 on first write. That is why this treats "absent" as 0 rather than
 * starting at some arbitrary base: a legacy row must not appear to be newer
 * than a row another device has been editing since.
 */
export function nextVersion(current: number | undefined): number {
  return (current ?? 0) + 1;
}

/*
 * REMOVED: `changeIdFor(entityType, entityId, version)`.
 *
 * It derived the id from the row id and the version, and a version is not an
 * identity: two devices that each edit the same row from the same base BOTH
 * compute version 2, so the function handed two DIFFERENT mutations the SAME id.
 * The provider kept the first payload and collapsed the second — silently,
 * because from its side they were one mutation. That is last-write-wins arriving
 * through the idempotency key instead of through the merge rule, which is the one
 * place this project forbids it.
 *
 * Change ids are now minted once per mutation, in this file, and PERSISTED on the
 * outbox row beside the payload. See `docs/sync-architecture.md` §10.
 */
